// ExtensionMonitor.js — Monitor de Ramais em cards (estilo FOP2).
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import API_URL from './config';
import './ExtensionMonitor.css';

function ExtensionMonitor({ token, userRole }) {
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30); // segundos
  const [timeUntilRefresh, setTimeUntilRefresh] = useState(refreshInterval);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [serverId, setServerId] = useState('');
  const [availableServers, setAvailableServers] = useState([]);
  const isAdmin = userRole === 'admin';

  const isMounted = useRef(true);

  const formatDateTime = () => {
    const now = new Date();
    return now.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const fetchExtensions = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setError('');
    try {
      const url = isAdmin && serverId
        ? `${API_URL}/extensions/status?serverId=${serverId}`
        : `${API_URL}/extensions/status`;
      setLoading(true);
      const response = await axios.get(url, { headers: { Authorization: 'Bearer ' + token } });
      if (isMounted.current) {
        setExtensions(response.data);
        setLastUpdated(formatDateTime());
        setSuccess('Ramais atualizados com sucesso!');
        setTimeout(() => { if (isMounted.current) setSuccess(''); }, 3000);
      }
    } catch (err) {
      if (isMounted.current) {
        setError(`Erro ao buscar status dos ramais: ${err.response?.data?.message || err.message}`);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // Buscar servidores disponíveis para admin
  useEffect(() => {
    if (!isAdmin) return;
    const fetchServers = async () => {
      try {
        const resp = await axios.get(`${API_URL}/users/servers/active`, {
          headers: { Authorization: 'Bearer ' + token }
        });
        if (Array.isArray(resp.data) && resp.data.length > 0) {
          setAvailableServers(resp.data);
          setServerId(resp.data[0].id.toString());
        }
      } catch (e) {
        console.error('Erro ao buscar servidores:', e);
      }
    };
    fetchServers();
  }, [isAdmin, token]);

  // Carregar ramais quando o servidor mudar
  useEffect(() => {
    fetchExtensions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverId]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;
    const countdown = setInterval(() => {
      setTimeUntilRefresh((prev) => {
        if (prev <= 1) {
          fetchExtensions();
          return refreshInterval;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, refreshInterval]);

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
    setTimeUntilRefresh(refreshInterval);
  };

  const getCardClass = (state) => {
    if (state === 'inuse') return 'extCardInuse';
    if (state === 'online') return 'extCardOnline';
    return 'extCardOffline';
  };

  const getStateLabel = (state) => {
    if (state === 'inuse') return 'Em uso';
    if (state === 'online') return 'Conectado';
    return 'Desconectado';
  };

  const filteredExtensions = extensions.filter((ext) => {
    if (!filterText) return true;
    return (
      ext.extension.includes(filterText) ||
      (ext.ipAddress && ext.ipAddress.includes(filterText)) ||
      (ext.status && ext.status.toLowerCase().includes(filterText.toLowerCase()))
    );
  });

  return (
    <div className="extensionMonitorContainer">
      <h1 className="pageTitle">Monitor de Ramais</h1>

      {success && <div className="message messageSuccess">{success}</div>}
      {error && <div className="message messageError">{error}</div>}

      <div className="controlsSection">
        <div className="lastUpdatedInfo">
          {lastUpdated && <span>Última atualização: {lastUpdated}</span>}
        </div>

        {isAdmin && availableServers.length > 0 && (
          <div className="serverSelector">
            <label htmlFor="ext-server-select">PBX:</label>
            <select
              id="ext-server-select"
              value={serverId}
              onChange={(e) => setServerId(e.target.value)}
              className="formSelect"
            >
              {availableServers.map((server) => (
                <option key={server.id} value={server.id.toString()}>
                  {server.name || `PBX ${server.id}`}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="controlButtons">
          <button onClick={fetchExtensions} disabled={loading || refreshing} className="button buttonPrimary">
            {loading ? 'Atualizando...' : 'Atualizar Agora'}
          </button>
          <button onClick={toggleAutoRefresh} className={`button ${autoRefresh ? 'buttonDanger' : 'buttonSecondary'}`}>
            {autoRefresh ? `Parar Atualização (${timeUntilRefresh}s)` : 'Atualização Automática'}
          </button>
        </div>

        <div className="filterContainer">
          <input
            type="text"
            placeholder="Filtrar ramais por número, IP ou status..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="filterInput"
          />
          {filterText && (
            <button onClick={() => setFilterText('')} className="clearFilterButton">×</button>
          )}
        </div>
      </div>

      {loading && <div className="loadingMessage">Carregando status dos ramais...</div>}

      {!loading && filteredExtensions.length > 0 && (
        <div className="extCardsGrid">
          {filteredExtensions.map((ext) => (
            <div key={ext.extension} className={`extCard ${getCardClass(ext.state)}`}>
              <div className="extCardNumber">{ext.extension}</div>
              {ext.name && (
                <div className="extCardName" title={ext.name}>{ext.name}</div>
              )}
              <div className="extCardStatus">{getStateLabel(ext.state)}</div>
              <div className="extCardDetail">{ext.status}</div>
              <div className="extCardIp">{ext.ipAddress}</div>
            </div>
          ))}
        </div>
      )}

      {!loading && filteredExtensions.length === 0 && (
        <div className="noDataMessage">
          {filterText
            ? 'Nenhum ramal corresponde ao filtro aplicado.'
            : 'Nenhum ramal encontrado. Verifique se você tem permissões para visualizar ramais.'}
        </div>
      )}

      <div className="extLegend">
        <span className="legendItem"><span className="legendDot legendOnline" /> Conectado</span>
        <span className="legendItem"><span className="legendDot legendInuse" /> Em uso</span>
        <span className="legendItem"><span className="legendDot legendOffline" /> Desconectado</span>
      </div>
    </div>
  );
}

export default ExtensionMonitor;
